import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { ThemeToggle } from "../sirius/ThemeProvider";
import { LanguageToggle } from "../i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Signup() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Carry the cadence (monthly/yearly) chosen on the landing pricing toggle
  // through signup → account checkout so it lands on the right Stripe price.
  const cadence = (new URLSearchParams(location.search).get("cadence") || "monthly").toLowerCase();
  const safeCadence = (cadence === "yearly") ? "yearly" : "monthly";
  const returnTo = new URLSearchParams(location.search).get("return") || `/account?welcome=1&cadence=${safeCadence}`;

  // Optional referral code: prefer `?ref=CODE` from URL; user can also type it.
  const urlRef = (new URLSearchParams(location.search).get("ref") || "").toUpperCase();
  const [referralCode, setReferralCode] = useState(urlRef);
  const [referralInfo, setReferralInfo] = useState(null);  // null=untouched, {valid,referrer_name,trial_days} | {valid:false}

  // Validate referral code as the user types (debounced via a small delay).
  useEffect(() => {
    const code = referralCode.trim();
    if (!code) { setReferralInfo(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/referrals/check/${encodeURIComponent(code)}`);
        if (cancelled) return;
        if (r.ok) setReferralInfo(await r.json());
        else setReferralInfo({ valid: false });
      } catch { if (!cancelled) setReferralInfo({ valid: false }); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [referralCode]);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (!acceptedTerms) {
      setErr("Du må godta vilkårene og personvernerklæringen for å fortsette.");
      return;
    }
    setBusy(true);
    try {
      await signup(email.trim(), password, name.trim(), referralCode.trim() || null, { acceptedTerms: true });
      navigate(returnTo);
    } catch (e) {
      setErr(e.message || "Kunne ikke opprette konto");
    } finally {
      setBusy(false);
    }
  }

  const trialDays = referralInfo?.valid ? referralInfo.trial_days : 7;

  return (
    <div className="srs" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }} data-testid="signup-root">
      <nav style={navBar}>
        <Link to="/" style={brandLink} data-testid="brand-mark">
          <span className="srs-logo-dot" />
          <span className="srs-display" style={{ fontSize: 24, fontWeight: 500 }}>EuroPilot</span>
        </Link>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <LanguageToggle />
          <ThemeToggle />
        </div>
      </nav>

      <main style={shellMain}>
        <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 56, maxWidth: 1040, width: "100%", alignItems: "center" }} className="signup-grid">
          <div>
            <div className="srs-eyebrow" style={{ marginBottom: 14 }}>// REGISTRER DEG</div>
            <h1 className="srs-display" style={{ fontSize: "clamp(40px, 5.5vw, 72px)", lineHeight: 1, letterSpacing: "-0.03em", margin: 0 }}>
              {trialDays} dager gratis. <em style={{ color: "var(--srs-sun)" }}>Deretter 19 kr første 2 mnd, så 29 kr/mnd.</em>
            </h1>
            {referralInfo?.valid && (
              <div style={{ marginTop: 16, padding: "10px 14px", background: "var(--srs-sand)", color: "var(--srs-sand-deep)", borderRadius: 12, fontSize: 13, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 8 }} data-testid="referral-applied">
                ✓ Invitert av <strong>{referralInfo.referrer_name}</strong> — utvidet prøveperiode aktivert
              </div>
            )}
            <p style={{ marginTop: 24, fontSize: 16, color: "var(--srs-ink-dim)", lineHeight: 1.65, maxWidth: 440 }}>
              Avgift inkluderer 25% MVA. Vi henter kortdetaljer ved registrering, men trekker ikke noe før prøveperioden er over. Avbryt når som helst.
            </p>
            <ul style={{ marginTop: 28, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12, fontSize: 14, color: "var(--srs-ink-dim)" }}>
              {["Hver gang du handler — vi viser EuroBonus-rate og Trumf-cashback", "Konverteringskalkulator: 1 NOK Trumf = 13,5 EB-poeng", "Norsk Bokmål-grensesnitt, fungerer på 200+ butikker"].map(t => (
                <li key={t} style={{ display: "flex", gap: 10 }}>
                  <span style={{ color: "var(--srs-good)", fontWeight: 700 }}>✓</span>{t}
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={submit} className="srs-card" style={{ padding: 36, borderRadius: 24 }} data-testid="signup-form">
            <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-ink-dim)", marginBottom: 18 }}>OPPRETT KONTO</div>
            <label style={labelCss}>Navn (valgfritt)</label>
            <input style={inputCss} type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ola Nordmann" data-testid="signup-name" autoComplete="name" />

            <label style={{ ...labelCss, marginTop: 14 }}>E-post</label>
            <input style={inputCss} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ola@example.no" data-testid="signup-email" autoComplete="email" />

            <label style={{ ...labelCss, marginTop: 14 }}>Passord (min 8 tegn)</label>
            <input style={inputCss} type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="signup-password" autoComplete="new-password" />

            <label style={{ ...labelCss, marginTop: 14 }}>Referansekode (valgfritt)</label>
            <input
              style={{ ...inputCss, fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", borderColor: referralInfo?.valid ? "var(--srs-sun)" : referralInfo === null || !referralCode ? "var(--srs-line)" : "var(--srs-warn)" }}
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              placeholder="EP-XXXXXX"
              data-testid="signup-referral"
              maxLength={12}
            />
            {referralCode && referralInfo && !referralInfo.valid && (
              <div style={{ marginTop: 6, fontSize: 11, color: "var(--srs-warn)" }} data-testid="referral-invalid">Ugyldig referansekode</div>
            )}
            {referralInfo?.valid && (
              <div style={{ marginTop: 6, fontSize: 11, color: "var(--srs-sun)", fontWeight: 600 }} data-testid="referral-valid">+ {referralInfo.trial_days - 7} dager ekstra prøveperiode</div>
            )}

            {err && <div style={errCss} data-testid="signup-error">{err}</div>}

            <label style={{ marginTop: 18, display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", fontSize: 12, lineHeight: 1.5, color: "var(--srs-ink-dim)" }} data-testid="terms-label">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                required
                data-testid="signup-terms-checkbox"
                style={{ marginTop: 2, width: 16, height: 16, accentColor: "var(--srs-sun)", flexShrink: 0 }}
              />
              <span>
                Jeg godtar EuroPilot sine{" "}
                <Link to="/terms" target="_blank" rel="noopener" style={{ color: "var(--srs-sun)", textDecoration: "underline", textUnderlineOffset: 3 }} data-testid="link-terms">vilkår</Link>
                {" og "}
                <Link to="/privacy" target="_blank" rel="noopener" style={{ color: "var(--srs-sun)", textDecoration: "underline", textUnderlineOffset: 3 }} data-testid="link-privacy">personvernerklæring</Link>
                {" (GDPR)."}
              </span>
            </label>

            <button type="submit" disabled={busy || !acceptedTerms} className="srs-cta" style={{ marginTop: 16, width: "100%", justifyContent: "center" }} data-testid="signup-submit">
              {busy ? "Oppretter…" : `Start ${trialDays} dager gratis →`}
            </button>

            <div style={{ marginTop: 18, fontSize: 12, color: "var(--srs-ink-faint)", textAlign: "center" }}>
              Har du allerede en konto? <Link to="/login" style={{ color: "var(--srs-sun)", textDecoration: "none", fontWeight: 600 }} data-testid="signup-to-login">Logg inn</Link>
            </div>
          </form>
        </div>
      </main>

      <style>{`
        @media (max-width: 900px) {
          .signup-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
        }
      `}</style>
    </div>
  );
}

const navBar = { position: "sticky", top: 0, zIndex: 30, background: "color-mix(in srgb, var(--srs-bg) 88%, transparent)", backdropFilter: "blur(14px)", borderBottom: "1px solid var(--srs-line)", padding: "16px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" };
const brandLink = { display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: "var(--srs-ink)" };
const shellMain = { flex: 1, padding: "60px 28px 80px", display: "grid", placeItems: "center" };
const labelCss = { display: "block", fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: "0.22em", fontWeight: 600, color: "var(--srs-ink-dim)", textTransform: "uppercase", marginBottom: 8 };
const inputCss = { width: "100%", background: "var(--srs-surface-2)", border: "1px solid var(--srs-line)", borderRadius: 10, padding: "12px 14px", fontSize: 14, color: "var(--srs-ink)", fontFamily: "inherit", outline: "none" };
const errCss = { marginTop: 16, padding: "10px 12px", background: "color-mix(in srgb, var(--srs-warn) 12%, transparent)", border: "1px solid var(--srs-warn)", borderRadius: 10, color: "var(--srs-warn)", fontSize: 12, lineHeight: 1.5 };
